import { ICart, ICartAddon, ICartOffer } from "@features/cart/ICart";
import { IFlyingCart, IFlyingCartItem, IFlyingCartOffer } from "@features/cart/IFlyingCart";

/**
 * Maps offer usages
 */
const mapUsages = (usages: ICartOffer["baseOffer"]["usages"]) => {
    return usages.map((usage) => ({
        description: usage.description,
        displayOrder: usage.displayOrder,
    }));
};

/**
 * Maps port information if it exists
 */
const mapPortInfo = (port?: ICartOffer["port"]) => {
    if (!port) {
        return undefined;
    }

    return {
        phoneNumber: port.phoneNumber,
        portStartDateTime: port.portStartDateTime,
    };
};

/**
 * Maps amounts
 */
const mapAmounts = (recurringChargeAmount: number, upFrontChargeAmount: number) => ({
    recurringAmount: recurringChargeAmount,
    upFrontAmount: upFrontChargeAmount,
});

/**
 * Maps an individual offer
 */
const mapOffer = (offer: ICartOffer, offerQuantity = 1): IFlyingCartOffer => ({
    offerId: offer.offerId,
    quantity: offerQuantity,
    baseOffer: {
        catalogOfferCode: offer.baseOffer.catalogOfferCode,
        description: offer.baseOffer.description,
        comment: offer.baseOffer.comment,
        displayOrder: offer.baseOffer.displayOrder,
        amountVatIncluded: mapAmounts(offer.baseOffer.recurringChargeAmount, offer.baseOffer.upFrontChargeAmount),
        usages: mapUsages(offer.baseOffer.usages),
        serviceGroup: offer.baseOffer.serviceGroup,
    },
    amountVatIncluded: mapAmounts(offer.recurringChargeAmount, offer.upFrontChargeAmount),
    cartItems: mapCartItems(offer, offerQuantity),
    port: mapPortInfo(offer.port),
    simType: offer.simType,
});

const mapCartItems = (offer: ICartOffer, offerQuantity = 1): IFlyingCartItem[] => {
    const cartItems: IFlyingCartItem[] = [];
    cartItems.push(mapOfferToCartItem(offer, offerQuantity));

    if (offer.addOns && offer.addOns.length > 0) {
        cartItems.push(mapAddOnToCartItem(offer.addOns));
    }

    return cartItems;
};

const mapOfferToCartItem = (offer: ICartOffer, offerQuantity = 1): IFlyingCartItem => ({
    description: offer.baseOffer.description,
    monthlyAmount: offer.baseOffer.recurringChargeAmount * offerQuantity,
    upFrontAmount: offer.baseOffer.upFrontChargeAmount * offerQuantity,
    quantity: offerQuantity,
});

const mapAddOnToCartItem = (addOns: ICartAddon[]): IFlyingCartItem => ({
    description: addOns[0].description,
    monthlyAmount: addOns.reduce((sum, addOn) => sum + addOn.amountVatIncluded.recurringAmount.amount, 0),
    upFrontAmount: 0,
    quantity: addOns.length,
});

/**
 * Maps ICart data to the format expected by FlyingCart
 * Merges identical offers
 */
export const mapCartToFlyingCart = (cart: ICart): IFlyingCart => {
    // Group offers by name to calculate quantities
    const offerGroups = new Map<string, ICartOffer[]>();

    cart.offers.forEach((offer) => {
        const key = offer.baseOffer.description;
        if (!offerGroups.has(key)) {
            offerGroups.set(key, []);
        }
        offerGroups.get(key)!.push(offer);
    });

    // Map each group with its quantity
    const mappedOffers: IFlyingCartOffer[] = [];

    offerGroups.forEach((groupOffers) => {
        if (groupOffers.length === 1) {
            // Single offer
            mappedOffers.push(mapOffer(groupOffers[0], 1));
        } else {
            // Multiple identical offers, merge amounts
            const firstOffer = groupOffers[0];
            const totalRecurring = groupOffers.reduce((sum, offer) => sum + offer.recurringChargeAmount, 0);
            const totalUpFront = groupOffers.reduce((sum, offer) => sum + offer.upFrontChargeAmount, 0);

            const mergedOffer: ICartOffer = {
                ...firstOffer,
                recurringChargeAmount: totalRecurring,
                upFrontChargeAmount: totalUpFront,
            };
            mappedOffers.push(mapOffer(mergedOffer, groupOffers.length));
        }
    });

    return {
        nbrAdditionalAllowedMobileSubscriptions: cart.nbrAdditionalAllowedMobileSubscriptions,
        offers: mappedOffers,
        totalRecurringAmount: cart.recurringChargeAmount,
        totalUpFrontAmount: cart.upFrontChargeAmount,
    };
};
